/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { ReactElement, useEffect, useState } from "react"
import { Button, useBreakpointComponent } from "@carrotfertility/carotene"
import { PageOption } from "components/dynamic-content/util/dynamicContent"
import { GuidanceElement } from "lib/contentful/components/GuidanceElement"
import { Paths } from "utils/Paths"
import { Redirect, Route, Switch, useHistory } from "react-router-dom"
import TalkToCarrotLocationRouter from "components/talk-to-carrot/TalkToCarrotLocationRouter"
import { TalkToCarrotContext } from "components/talk-to-carrot/TalkToCarrotContext"
import TwoColumnRightPrimary from "components/views/atoms/layouts/TwoColumnRightPrimary"
import styled from "styled-components"
import {
  getAccountDeletionPath,
  getGetYourCarrotPlanPath,
  getHelpPath,
  getProviderEligibilityPath,
  getRefreshCarrotPlanPath,
  HelpNode,
  NeedHelpLink,
  RootBreadcrumb,
  SmallHeaderOverride
} from "components/talk-to-carrot/util/talkToCarrotMolecules"
import { HelpPage } from "components/talk-to-carrot/TalkToCarrotNeedHelp"
import { getTTCLocationOption } from "lib/contentful/utils/mappingUtils"
import { TalkToCarrotLegal } from "components/talk-to-carrot/TalkToCarrotLegal"
import { getTalkToCarrotLocationGuidance, getTalkToCarrotLocations } from "../../lib/contentful/contentfulClient"
import WorldVectorMap from "../../components/talk-to-carrot/TalkToCarrotMap"
import Guidance from "../../components/views/atoms/Guidance"
import { PageHeader } from "../../components/views/molecules/Molecules"
import { MasterLayout } from "../../components/views/atoms/layouts/MasterLayout"
import { Spacer } from "../../components/views/atoms/Atoms"
import { ProviderEligibility } from "./provider-eligibility"
import { RefreshCarrotPlan } from "./refresh-carrot-plan"
import { GetYourCarrotPlan } from "./get-your-carrot-plan"
import { TalkToCarrotSelectOption } from "../../components/talk-to-carrot/TalkToCarrotSelectOption"
import { Autocomplete, TextField } from "@carrotfertility/carotene-core"
import { DeleteAccount } from "./delete-account"
import { isIOSNativeApp } from "lib/carrot-api/MobileNativeClient"
import { useSelector } from "react-redux"
import { getPreferredLocale } from "../../reducers/userInfo"
import { useIntl, FormattedMessage } from "react-intl"
import { useIntlSort } from "utils/hooks/useIntlSort"

interface LocalizedPageOption extends PageOption {
  formattedLabel: string
}

const LocationFooter = styled.div.attrs({ className: "flex items-center w-100 justify-center" })`
  overflow: auto;
`

const TalkToCarrotLocations = (): JSX.Element => {
  const history = useHistory()
  const [talkToCarrotLocations, setTalkToCarrotLocations] = useState(null)
  const [selectedLocation, setSelectedLocation] = useState<PageOption>(null)
  const [hoveredLocation, setHoveredLocation] = useState<PageOption>(null)
  const [guidance, setGuidance] = useState<ReactElement<typeof Guidance>>(null)
  const preferredLocale = useSelector(getPreferredLocale)
  const intl = useIntl()
  const textfieldPlaceholder = intl.formatMessage({
    defaultMessage: "Select option"
  })
  const textfieldName = intl.formatMessage({
    defaultMessage: "location"
  })
  const { localeComparator } = useIntlSort()

  const formatLocations = (option: PageOption): LocalizedPageOption => {
    return {
      ...option,
      formattedLabel: intl.formatDisplayName(option.value, { type: "region" })
    }
  }

  const addHover = (option: LocalizedPageOption): LocalizedPageOption => {
    return {
      ...option,
      onMouseOver: (): void => setHoveredLocation(option),
      onMouseOut: (): void => setHoveredLocation(null)
    }
  }

  useEffect(() => {
    const loadTalkToCarrotLocations = async (): Promise<void> => {
      const talkToCarrotLocations = await getTalkToCarrotLocations()
      const talkToCarrotLocationOptions = talkToCarrotLocations
        ?.map(getTTCLocationOption)
        .map(formatLocations)
        .map(addHover)
        .filter(Boolean)
        .sort(({ formattedLabel: labelA }, { formattedLabel: labelB }) => localeComparator(labelA, labelB))
      setTalkToCarrotLocations(talkToCarrotLocationOptions)
    }
    if (!talkToCarrotLocations) {
      loadTalkToCarrotLocations()
    }

    const loadTalkToCarrotLocationGuidanceProps = async (): Promise<void> => {
      const guidance = await getTalkToCarrotLocationGuidance(preferredLocale)
      setGuidance(<GuidanceElement guidance={guidance} />)
    }
    if (!guidance) {
      loadTalkToCarrotLocationGuidanceProps()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onSubmit = (): void => {
    history.push(`${Paths.TALK_TO_CARROT}/${selectedLocation.slug}`)
  }

  const selectedOptionToTop = (locationOption: LocalizedPageOption): void => {
    setTalkToCarrotLocations(
      locationOption
        ? [
            locationOption,
            ...talkToCarrotLocations
              .filter(({ label }) => label !== locationOption.label)
              .sort(({ formattedLabel: labelA }, { formattedLabel: labelB }) => localeComparator(labelA, labelB))
          ]
        : talkToCarrotLocations
    )
  }

  function handleSelectLocation(_event, locationOption): void {
    setHoveredLocation(null)
    setSelectedLocation(locationOption)
    selectedOptionToTop(locationOption)
  }

  const continueButton = (
    <div className={"ml-auto"}>
      <Button onClick={onSubmit} disabled={!selectedLocation}>
        <FormattedMessage defaultMessage="Continue" />
      </Button>
    </div>
  )

  const continueWithHelp = (
    <>
      {<NeedHelpLink baseUrl={Paths.TALK_TO_CARROT} />}
      {continueButton}
    </>
  )

  const locationSelector = (
    <Autocomplete
      disablePortal
      multiple={false}
      onChange={handleSelectLocation}
      value={selectedLocation}
      getOptionLabel={(option) => option.formattedLabel}
      options={talkToCarrotLocations || []}
      clearOnEscape
      renderOption={(props, state) => (
        <TalkToCarrotSelectOption
          {...props}
          key={state.value}
          isSelected={state.value === selectedLocation?.value}
          onMouseOver={(...args) => {
            state.onMouseOver(...args)
          }}
          onMouseOut={state.onMouseOut}
        >
          {state.formattedLabel}
        </TalkToCarrotSelectOption>
      )}
      renderInput={(params) => (
        <TextField placeholder={textfieldPlaceholder} name={textfieldName} helperText=" " {...params} />
      )}
    />
  )
  const PickerFooter = ({ withHelp = false }: { withHelp?: boolean }): JSX.Element => (
    <LocationFooter>{withHelp ? continueWithHelp : continueButton}</LocationFooter>
  )

  const map = (
    <WorldVectorMap
      selectedLocationId={selectedLocation ? selectedLocation.value.toLowerCase() : ""}
      hoveredLocationId={hoveredLocation ? hoveredLocation.value.toLowerCase() : ""}
      onMouseEnter={({ target }: any): void =>
        setHoveredLocation(talkToCarrotLocations.find(({ value }) => value.toLowerCase() === target.id.toLowerCase()))
      }
      onMouseLeave={(): void => setHoveredLocation(null)}
      onClick={(event: any): void => {
        const optionClicked = talkToCarrotLocations.find(
          ({ value }) => value.toLowerCase() === event?.target.id.toLowerCase()
        )
        if (optionClicked) {
          handleSelectLocation(event, optionClicked)
        }
      }}
    />
  )

  const locationPickerWithMap = (
    <div className="w-100">
      {map}
      {locationSelector}
      <PickerFooter />
    </div>
  )
  const locationPickerWithHelp = (
    <div className="w-100">
      {locationSelector}
      <PickerFooter withHelp />
    </div>
  )

  const GuidanceWithHelp = ({ guidance }: { guidance: ReactElement<typeof Guidance> }): JSX.Element => (
    <>
      {guidance}
      {<HelpNode baseUrl={Paths.TALK_TO_CARROT} />}
    </>
  )

  const largeContent = (
    <TwoColumnRightPrimary
      secondaryContent={<GuidanceWithHelp guidance={guidance} />}
      primaryContent={locationPickerWithMap}
    />
  )

  const mediumContent = (
    <>
      {guidance}
      <Spacer height={3} />
      {map}
      {locationPickerWithHelp}
    </>
  )

  const smallContent = (
    <>
      <SmallHeaderOverride>
        {guidance}
        <Spacer height={2} />
        {locationPickerWithHelp}
      </SmallHeaderOverride>
    </>
  )

  const responsiveContent = useBreakpointComponent([smallContent, smallContent, mediumContent, largeContent])

  const masterLayoutTitle = intl.formatMessage({
    defaultMessage: "Talk to Carrot"
  })

  const guidedLocationPicker = (
    <>
      <MasterLayout
        footer={<TalkToCarrotLegal />}
        header={<PageHeader icon="icn-talk-to-carrot" title={masterLayoutTitle} />}
      >
        {guidance && responsiveContent}
      </MasterLayout>
    </>
  )

  return (
    <>
      {talkToCarrotLocations && (
        <TalkToCarrotContext.Provider value={{ talkToCarrotLocationOptions: talkToCarrotLocations }}>
          <Switch>
            <Route exact path={`${Paths.TALK_TO_CARROT}`} render={(): JSX.Element => guidedLocationPicker} />
            <Route
              exact
              path={getHelpPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => (
                <HelpPage breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]} baseUrl={Paths.TALK_TO_CARROT} />
              )}
            />
            <Route
              exact
              path={getProviderEligibilityPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => (
                <ProviderEligibility
                  breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]}
                  baseUrl={Paths.TALK_TO_CARROT}
                />
              )}
            />
            <Route
              exact
              path={getRefreshCarrotPlanPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => (
                <RefreshCarrotPlan
                  breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]}
                  baseUrl={Paths.TALK_TO_CARROT}
                />
              )}
            />
            <Route
              exact
              path={getGetYourCarrotPlanPath({ baseUrl: Paths.TALK_TO_CARROT })}
              render={(): JSX.Element => <GetYourCarrotPlan baseUrl={Paths.TALK_TO_CARROT} />}
            />

            {isIOSNativeApp() && (
              <Route
                exact
                path={getAccountDeletionPath({ baseUrl: Paths.TALK_TO_CARROT })}
                render={(): JSX.Element => (
                  <DeleteAccount
                    breadcrumbs={[<RootBreadcrumb key="rootBreadcrumb" />]}
                    baseUrl={Paths.TALK_TO_CARROT}
                  />
                )}
              />
            )}

            <Route path={`${Paths.TALK_TO_CARROT}/:locationSlug`} component={TalkToCarrotLocationRouter} />
            <Redirect from="*" to="/talk-to-carrot" />
          </Switch>
        </TalkToCarrotContext.Provider>
      )}
    </>
  )
}

export { TalkToCarrotLocations }
